// components/use-case-details/use-case-workflow/use-case-workflow-section-2.tsx
"use client";
// # components/use-case-details/use-case-workflow/use-case-workflow-section.tsx

import { motion } from 'framer-motion';
import React from 'react';

// Direct import of workflow components
import { getWorkflowComponentByType } from './workflow-component-registry';
import { WorkflowRenderer } from './workflow-renderer';

// Extended interface for Process Step that supports both old and new formats
interface WorkflowProcessStep {
  step_id?: string;
  actor?: string;
  description: string;
  inputs?: Array<{source_id: string; description: string}>;
  outputs?: Array<{destination_id: string; description: string}>;
  components?: Array<any>;
}

// Extended interfaces to support both old and new formats
interface ExtendedExample {
  title?: string;
  description?: string;
  // Old format
  input?: string;
  output?: string;
  // New format
  user_query?: string;
  ai_thinking_text?: string;
  ai_response_intro?: string;
  ai_response_outro?: string;
  results?: Array<any>;
}

// WorkflowStep component for displaying various types of workflow steps
/*
 * This component can be used to represent different types of workflow steps 
 * beyond just chat interactions. It supports:
 * 
 * - Displaying media (images or videos)
 * - Showing input/output examples in different formats
 * - Different step types that correspond to different visual styles
 * 
 * Example JSON for Text to Speech workflow:
 * {
 *   "type": "workflowStep",
 *   "title": "Audio Generation",
 *   "description": "Converts text to natural-sounding speech using advanced AI voice models",
 *   "stepType": "generation",
 *   "inputOutput": [
 *     {
 *       "description": "Example of converting formal text to audio",
 *       "input": "Welcome to our annual shareholder meeting. Today we'll discuss our financial performance.",
 *       "output": "[Audio file: formal_announcement.mp3]"
 *     }
 *   ],
 *   "mediaUrl": "/images/audio-waveform.gif"
 * }
 * 
 * Example JSON for Image Recognition workflow:
 * {
 *   "type": "workflowStep",
 *   "title": "Image Analysis",
 *   "description": "Detects objects and scenes in uploaded images",
 *   "stepType": "analysis",
 *   "input": "Uploaded image of a city skyline",
 *   "output": "Detected: buildings (86%), sky (95%), vehicles (34%), people (12%)",
 *   "mediaUrl": "/images/image-analysis-example.jpg"
 * }
 * 
 * Example JSON for Data Processing workflow:
 * {
 *   "type": "workflowStep",
 *   "title": "Data Transformation",
 *   "description": "Cleans and structures raw data for analysis",
 *   "stepType": "transformation",
 *   "inputOutput": [
 *     {
 *       "description": "Converting unstructured text to structured data",
 *       "input": "John Smith, 42, New York, Software Engineer",
 *       "output": "{\n  \"name\": \"John Smith\",\n  \"age\": 42,\n  \"location\": \"New York\",\n  \"occupation\": \"Software Engineer\"\n}"
 *     }
 *   ]
 * }
 */
export const WorkflowStep = ({ 
  title, 
  description, 
  icon, 
  stepType, 
  input, 
  output,
  mediaUrl,
  inputOutput,
  className = ""
}: { 
  title: string;
  description: string;
  icon?: React.ReactNode;
  stepType: 'data' | 'process' | 'analysis' | 'output' | 'automation' | 'transcription' | 'generation' | 'transformation';
  input?: string;
  output?: string;
  mediaUrl?: string;
  inputOutput?: Array<{input: string, output: string, description?: string}>;
  className?: string;
}) => {
  const getStepColor = (type: string) => {
    switch(type) {
      case 'data': return 'blue';
      case 'process': return 'purple';
      case 'analysis': return 'indigo';
      case 'output': return 'green';
      case 'automation': return 'cyan';
      case 'transcription': return 'amber';
      case 'generation': return 'pink';
      case 'transformation': return 'teal';
      default: return 'gray';
    }
  };
  
  const color = getStepColor(stepType);
  
  return (
    <motion.div
      className={`rounded-xl overflow-hidden bg-gray-800/90 border border-gray-700 shadow-lg ${className}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header with colored accent */}
      <div className="relative p-5 bg-gray-800 border-b border-gray-700">
        <div className={`absolute top-0 left-0 w-full h-1 bg-${color}-500`} />
        <div className="flex items-center">
          {icon && (
            <div className={`bg-${color}-500/20 p-3 rounded-lg mr-4 text-${color}-400`}>
              {icon}
            </div>
          )}
          <div>
            <h4 className="text-xl font-bold text-white">{title}</h4>
            <p className="text-sm text-gray-300 mt-1">{description}</p>
          </div>
        </div>
      </div>
      
      {/* Content Area */}
      <div className="p-6">
        {/* Media Content */}
        {mediaUrl && (
          <div className="mb-6 rounded-lg overflow-hidden">
            {mediaUrl.endsWith('.mp4') || mediaUrl.endsWith('.webm') ? (
              <video 
                className="w-full rounded-lg" 
                controls
                autoPlay={false}
                muted
                loop
              >
                <source src={mediaUrl} type={`video/${mediaUrl.split('.').pop()}`} />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img 
                src={mediaUrl} 
                alt={title} 
                className="w-full rounded-lg"
              />
            )}
          </div>
        )}
        
        {/* Input Output Examples */}
        {inputOutput && inputOutput.length > 0 && (
          <div className="space-y-6 mt-4">
            {inputOutput.map((item, index) => (
              <div key={index} className="bg-gray-900/70 rounded-lg p-4">
                {item.description && (
                  <p className="text-gray-300 mb-3 text-sm">{item.description}</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-800/80 p-3 rounded-lg">
                    <div className="text-xs text-gray-500 uppercase mb-2">Input</div>
                    <div className="text-gray-200 text-sm">{item.input}</div>
                  </div>
                  <div className="bg-gray-800/80 p-3 rounded-lg">
                    <div className="text-xs text-gray-500 uppercase mb-2">Output</div>
                    <div className="text-gray-200 text-sm">{item.output}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Simple Input/Output */}
        {(input || output) && !inputOutput && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {input && (
              <div className="bg-gray-900/70 p-4 rounded-lg">
                <div className="text-xs text-gray-500 uppercase mb-2">Input</div>
                <div className="text-gray-200">{input}</div>
              </div>
            )}
            {output && (
              <div className="bg-gray-900/70 p-4 rounded-lg">
                <div className="text-xs text-gray-500 uppercase mb-2">Output</div>
                <div className="text-gray-200">{output}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Helper function to determine actor icon/color
const getActorIconAndColor = (actor: string): {icon: 'ai' | 'human' | 'system', color: string} => {
  switch (actor?.toLowerCase()) {
    case 'ai':
      return { icon: 'ai', color: 'bg-green-600' };
    case 'human':
      return { icon: 'human', color: 'bg-blue-600' };
    default:
      return { icon: 'system', color: 'bg-gray-600' };
  }
};

// Helper function to render different component types
const renderComponent = (component: any, index: number) => {
  if (!component || !component.component_type) {
    return null;
  }

  // Extract the details object based on component type
  let details = null;
  let componentType = component.component_type;
  
  // Map component_type to the correct details object and normalize to match our component registry
  if (componentType === 'agent' && component.agent_details) {
    details = component.agent_details;
    componentType = 'agentCard';
  } else if (componentType === 'data_source' && component.data_source_details) {
    details = component.data_source_details;
    // Could map to a specific component type if you have one
  } else if (componentType === 'data_store' && component.data_store_details?.store_type === 'vector_db') {
    details = component.data_store_details;
    componentType = 'vectorDBComponent';
  } else if (componentType === 'tool' && component.tool_details) {
    details = component.tool_details;
    // Map to tool component
  } else if (componentType === 'human_task' && component.human_task_details) {
    details = component.human_task_details;
    componentType = 'hitlComponent';
  }

  // If we don't have details or can't map it, return an informational component
  if (!details) {
    return (
      <div key={`unknown-${index}`} className="bg-gray-800 border border-gray-700 p-4 rounded-lg mb-4">
        <p className="text-gray-300">Component: {component.component_type}</p>
        <pre className="text-xs text-gray-400 mt-2 overflow-auto max-h-40">
          {JSON.stringify(component, null, 2)}
        </pre>
      </div>
    );
  }

  // Get the appropriate component from the registry
  const Component = getWorkflowComponentByType(componentType);
  
  // For agent components
  if (componentType === 'agentCard') {
    return (
      <Component
        key={`agent-${index}`}
        title={details.title || 'Agent'}
        agentType={details.agent_type || 'analysis'}
        description={details.description || ''}
        bulletPoints={details.bullet_points || []}
        model={details.model || 'Unknown Model'}
        temperature={details.temperature?.toString() || '0.7'}
        trigger={details.trigger || 'Manual'}
        output={details.output || 'Analysis Results'}
        borderColor="bg-purple-600"
      />
    );
  }
  
  // For vector DB components
  if (componentType === 'vectorDBComponent') {
    return (
      <Component
        key={`vector-db-${index}`}
        title={details.title || 'Vector Database'}
        description={details.description || ''}
        databaseType={details.database_name || 'Unknown DB'}
        embeddingModel={details.embedding_model || 'Unknown Embedding'}
        dimensions={details.dimensions || 1536}
        metricType={details.metric_type || 'cosine'}
        exampleQuery="Sample query"
        exampleResult="Sample result"
        industryContext="General"
        borderColor="bg-blue-600"
        implementationSteps={[]}
        implementationTips={[]}
      />
    );
  }
  
  // For HITL components
  if (componentType === 'hitlComponent') {
    return (
      <Component
        key={`hitl-${index}`}
        title={details.title || 'Human Task'}
        description={details.description || ''}
        approvalProcess={[
          {
            stage: "Review",
            description: "Human expert reviews the AI output",
            actor: "human",
            decisionType: "binary"
          }
        ]}
        borderColor="bg-purple-600"
      />
    );
  }
  
  // Default fallback for unknown component types
  return (
    <div key={`component-${index}`} className="bg-gray-800 border border-gray-700 p-4 rounded-lg mb-4">
      <h3 className="text-lg font-semibold">{details.title || 'Component'}</h3>
      <p className="text-gray-300 text-sm">{details.description || ''}</p>
      <div className="mt-2">
        <span className="inline-block bg-gray-700 text-white px-2 py-1 rounded text-xs">
          {component.component_type}
        </span>
      </div>
    </div>
  );
};

// Update the WorkflowSection2Props interface to accept any workflow structure
interface WorkflowSection2Props {
  useCase: {
    workflows?: any[]; // Use more permissive type for workflows
    [key: string]: any;
  };
}

export default function WorkflowSection2({
  useCase,
}: WorkflowSection2Props) {
  // Handle case where useCase doesn't have workflows
  if (!useCase || !useCase.workflows || !Array.isArray(useCase.workflows) || useCase.workflows.length === 0) {
    return (
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 my-6">
        <h3 className="text-xl font-bold text-amber-500 mb-4">No Workflow Data Available</h3>
        <p className="text-gray-400">This use case does not have any workflow data defined.</p>
      </div>
    );
  }

  return (
    <div className="space-y-20">
      {useCase.workflows.map((workflow: any, index: number) => {
        // Skip invalid workflow data
        if (!workflow) return null;
        
        // Ensure we have a proper ID by checking both properties
        const workflowId = workflow.id || workflow.workflow_id || `workflow-${index + 1}`;
        
        return (
          <div key={workflowId} id={`workflow-${index + 1}`} className="space-y-8">
            {/* Workflow Header */}
            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="text-2xl font-bold text-white">
                {workflow.title || `Workflow ${index + 1}`}
              </h3>
              <p className="text-gray-300 mt-2">
                {workflow.description || "This workflow demonstrates an AI solution pattern."}
              </p>
            </div>
            
            {/* Workflow Content - Using WorkflowRenderer for a standardized display */}
            <div>
              <WorkflowRenderer workflow={workflow} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

